import { Timestamp } from "firebase/firestore/lite";
import {
  CampaignDetails,
  CampaignMode,
  DbBusinessCampaign,
  DbCampaignStatus,
  TemplateParamSection,
  CampaignChannel,
} from "./models";
import { EmailTemplate } from "../email/models";
import { ExtendedTemplate } from "../whatsapp/message-templates/provider-state";
import { CampaignTemplate } from "./provider-state";
import { DbBusinessClient } from "../client/models";
import { DbBusiness } from "../business/models";
import { campaignService } from "./service";
import { getTemplateParams } from "./provider-state";
import { setupProgressService } from "../business/setup-progress-service";
import { DbBusinessSetupStep } from "../business/models";
import { NextRouter } from "next/router";

export const filterClientsByEmail = (
  clients: DbBusinessClient[],
): DbBusinessClient[] => {
  return clients.filter((client) => client.email && client.email.trim() !== "");
};

export const mapToNotifiedUsers = (
  clients: Array<{ client_id?: string; id?: string; created_at?: Timestamp }>,
): Array<{ client_id: string; created_at: Timestamp }> => {
  return clients.map((client) => ({
    client_id: client.client_id || client.id || "",
    created_at: client.created_at || Timestamp.now(),
  }));
};

export const getEmailTemplateValues = (
  templateParams: CampaignDetails["templateParams"],
): Record<string, string> => {
  const templateValues: Record<string, string> = {};
  if (templateParams) {
    templateParams.forEach((param) => {
      const key = param.key.replace(/[{}]/g, "");
      templateValues[key] = param.value || "";
    });
  }
  return templateValues;
};

export const isEmailTemplate = (
  template: CampaignTemplate | null | undefined,
): template is EmailTemplate => {
  return (
    template !== null &&
    template !== undefined &&
    "htmlContent" in template &&
    "editableFields" in template
  );
};

export const prepareEmailCampaignData = (
  basicCampaignDetails: CampaignDetails,
  templateDetails: CampaignTemplate | null | undefined,
): DbBusinessCampaign => {
  const emailTemplateData = isEmailTemplate(templateDetails)
    ? templateDetails
    : null;
  const htmlContent = emailTemplateData?.htmlContent || "";
  const templateParams = basicCampaignDetails.templateParams || [];

  const templateValues = getEmailTemplateValues(templateParams);

  let processedHtmlContent = htmlContent;
  templateParams.forEach((param) => {
    const regex = new RegExp(param.key.replace(/[{}]/g, "\\$&"), "g");
    processedHtmlContent = processedHtmlContent.replace(
      regex,
      param.value || param.key,
    );
  });

  const notifiedUsers = mapToNotifiedUsers(
    basicCampaignDetails.notifyingClients,
  );

  return {
    id: "",
    name: basicCampaignDetails.name,
    description: basicCampaignDetails.description || "",
    template_id: templateDetails?.id || "",
    channel: CampaignChannel.Email,
    email_subject: basicCampaignDetails.emailSubject || "",
    email_sender: basicCampaignDetails.emailSender || "",
    email_template: processedHtmlContent,
    template_values: templateValues,
    filter: basicCampaignDetails.filter,
    notified_users: notifiedUsers,
    status: DbCampaignStatus.Draft,
    created_at: basicCampaignDetails.createdAt as Timestamp,
    updated_at: Timestamp.now(),
  };
};

export const prepareWhatsAppCampaignData = (
  basicCampaignDetails: CampaignDetails,
  templateDetails: CampaignTemplate | null | undefined,
): DbBusinessCampaign => {
  if (isEmailTemplate(templateDetails)) {
    throw new Error("prepareWhatsAppCampaignData called with EmailTemplate");
  }
  const whatsAppTemplate = templateDetails as ExtendedTemplate;
  let message = "";
  let templateParams: Array<{
    key: string;
    value: string;
    name?: string;
    section?: TemplateParamSection;
  }> = [];

  if (whatsAppTemplate) {
    const containerMeta = JSON.parse(whatsAppTemplate.containerMeta || "{}");
    message = containerMeta.data || "";

    const allParams = basicCampaignDetails.templateParams || [];
    const headerParamsOnly = allParams.filter(
      (p) => p.section === TemplateParamSection.Header,
    );
    const dataParamsOnly = allParams.filter(
      (p) => !p.section || p.section === TemplateParamSection.Data,
    );

    templateParams = [...headerParamsOnly, ...dataParamsOnly].map((param) => ({
      key: param.key,
      value: param.value || param.key,
      name: param.name,
      section:
        param.section ||
        (headerParamsOnly.find((p) => p.key === param.key)
          ? TemplateParamSection.Header
          : TemplateParamSection.Data),
    }));

    dataParamsOnly.forEach((param) => {
      message = message.replace(param.key, param.value || param.key);
    });
  }

  const notifiedUsers = mapToNotifiedUsers(
    basicCampaignDetails.notifyingClients,
  );

  return {
    id: "",
    name: basicCampaignDetails.name,
    description: basicCampaignDetails.description || "",
    template_id: whatsAppTemplate?.id || "",
    channel: CampaignChannel.WhatsApp,
    message,
    template_params: templateParams,
    media: basicCampaignDetails.media ? [basicCampaignDetails.media] : [],
    filter: basicCampaignDetails.filter,
    notified_users: notifiedUsers,
    status: DbCampaignStatus.Draft,
    created_at: basicCampaignDetails.createdAt as Timestamp,
    updated_at: Timestamp.now(),
  };
};

export const validateSendCampaign = (
  selectedBusiness: DbBusiness | null | undefined,
  templateDetails: CampaignTemplate | null | undefined,
  basicCampaignDetails: CampaignDetails,
  isEmailCampaign: boolean,
): boolean => {
  if (
    !selectedBusiness ||
    !templateDetails ||
    (!isEmailCampaign &&
      !isEmailTemplate(templateDetails) &&
      (templateDetails as ExtendedTemplate).templateType == "IMAGE" &&
      (!basicCampaignDetails.media?.media_id ||
        !basicCampaignDetails.media?.url)) ||
    !basicCampaignDetails.notifyingClients ||
    basicCampaignDetails.notifyingClients.length === 0
  ) {
    return false;
  }
  return true;
};

export const sendEmailTestCampaign = async (
  selectedBusiness: DbBusiness,
  templateDetails: CampaignTemplate,
  basicCampaignDetails: CampaignDetails,
  testEmail: string | null | undefined,
  setToastText: (text: string) => void,
): Promise<void> => {
  if (!isEmailTemplate(templateDetails)) return;
  if (!testEmail || testEmail.trim() === "") {
    return;
  }

  const testClients = [
    {
      id: "test",
      email: testEmail.trim(),
    },
  ];

  const templateValues = getEmailTemplateValues(
    basicCampaignDetails.templateParams,
  );

  await campaignService.sendEmailCampaign(
    selectedBusiness.id,
    testClients,
    templateDetails.id,
    "",
    CampaignMode.Test,
    basicCampaignDetails.emailSender,
    basicCampaignDetails.emailSubject,
    templateValues,
  );

  setToastText("Test email sent successfully!");
};

export const sendWhatsAppTestCampaign = async (
  selectedBusiness: DbBusiness,
  templateDetails: CampaignTemplate,
  basicCampaignDetails: CampaignDetails,
  testPhone: string | null | undefined,
  whatsappCredits: number,
  setShowBuyCreditModal: (show: boolean) => void,
): Promise<void> => {
  if (isEmailTemplate(templateDetails)) return;
  const whatsAppTemplate = templateDetails as ExtendedTemplate;
  if (whatsappCredits < 1) {
    setShowBuyCreditModal(true);
    return;
  }

  const phoneNumbers = [
    {
      id: "123456789",
      phone: testPhone ?? "",
    },
  ];

  await campaignService.sendWhatsappCampaign(
    selectedBusiness.id,
    phoneNumbers,
    whatsAppTemplate.id,
    "",
    getTemplateParams(basicCampaignDetails),
    CampaignMode.Test,
    whatsAppTemplate.templateType == "IMAGE"
      ? basicCampaignDetails.media?.url
      : "",
    whatsAppTemplate.templateType == "IMAGE"
      ? basicCampaignDetails.media?.media_id
      : "",
    [],
  );
};

export const sendEmailCampaignFlow = async (
  selectedBusiness: DbBusiness,
  templateDetails: CampaignTemplate,
  basicCampaignDetails: CampaignDetails,
  clients: DbBusinessClient[],
  router: NextRouter,
  setShowSendCampaignModal: (show: boolean) => void,
  clearCampaignDetails: () => void,
): Promise<void> => {
  if (!isEmailTemplate(templateDetails)) return;
  const clientsWithEmail = filterClientsByEmail(clients).map((client) => ({
    id: client.id,
    email: client.email as string,
  }));

  if (!basicCampaignDetails.id || clientsWithEmail.length === 0) {
    return;
  }

  const templateValues = getEmailTemplateValues(
    basicCampaignDetails.templateParams,
  );

  await campaignService.sendEmailCampaign(
    selectedBusiness.id,
    clientsWithEmail,
    templateDetails.id,
    basicCampaignDetails.id,
    CampaignMode.Send,
    basicCampaignDetails.emailSender,
    basicCampaignDetails.emailSubject,
    templateValues,
  );

  await campaignService.updateCampaignClientsAndStatus(
    selectedBusiness.id,
    basicCampaignDetails.id,
    basicCampaignDetails.notifyingClients,
    DbCampaignStatus.Sent,
  );

  setShowSendCampaignModal(false);
  router.push("/campaigns");
  clearCampaignDetails();
};

export const sendWhatsAppCampaignFlow = async (
  selectedBusiness: DbBusiness,
  templateDetails: CampaignTemplate,
  basicCampaignDetails: CampaignDetails,
  clients: DbBusinessClient[],
  whatsappCredits: number,
  router: NextRouter,
  setShowSendCampaignModal: (show: boolean) => void,
  setShowBuyCreditModal: (show: boolean) => void,
  clearCampaignDetails: () => void,
): Promise<void> => {
  if (isEmailTemplate(templateDetails)) return;
  const whatsAppTemplate = templateDetails as ExtendedTemplate;
  if (whatsappCredits < clients.length) {
    setShowSendCampaignModal(false);
    setShowBuyCreditModal(true);
    return;
  }

  if (!basicCampaignDetails.id) {
    return;
  }

  const clientsWithPhone = clients.map((client) => ({
    id: client.id,
    phone: client.phone,
  }));

  const campaignsCounts = await campaignService.getBusinessCampaigns(
    selectedBusiness.id,
  );
  const sentCampaignsCount = campaignsCounts.filter(
    (c) => c.status === DbCampaignStatus.Sent,
  ).length;

  await campaignService.sendWhatsappCampaign(
    selectedBusiness.id,
    clientsWithPhone,
    whatsAppTemplate.id,
    basicCampaignDetails.id,
    getTemplateParams(basicCampaignDetails),
    CampaignMode.Send,
    whatsAppTemplate.templateType == "IMAGE"
      ? basicCampaignDetails.media?.url
      : "",
    whatsAppTemplate.templateType == "IMAGE"
      ? basicCampaignDetails.media?.media_id
      : "",
  );

  await campaignService.updateCampaignClientsAndStatus(
    selectedBusiness.id,
    basicCampaignDetails.id,
    basicCampaignDetails.notifyingClients,
    DbCampaignStatus.Sent,
  );

  if (sentCampaignsCount === 0) {
    await setupProgressService.removeSetupStep(
      selectedBusiness.id,
      DbBusinessSetupStep.SendCampaign,
    );
  }

  router.push("/campaigns");
  clearCampaignDetails();
};
